import { VFXEffectConfig } from '../../VFXEffectRegistry';
import { groundZone, zoneRing, centerpiece } from './zoneBuilders';

// =============================================================================
// RANGER EFFECTS
// =============================================================================

/**
 * Arrow whiz - wind streak and spark for basic arrow projectile
 */
const ARROW_WHIZ: VFXEffectConfig = {
  id: 'arrow_whiz',
  name: 'Arrow Whiz',
  category: 'burst',
  description: 'Wind streak and spark for basic arrow projectile',
  primitives: [
    // Motion streak line (warm gold, fading behind the arrow)
    {
      type: 'rect',
      width: 30,
      height: 2,
      color: 0xD4A574,
      alpha: 0.6,
      depth: 90,
      tween: {
        alpha: 0,
        scaleX: 2.5,
        scaleY: 0.3,
        duration: 200,
        ease: 'Power2'
      }
    },
    // Small wind puff particles (white, scattered)
    {
      type: 'circle',
      radius: 3,
      color: 0xFFFFFF,
      alpha: 0.5,
      depth: 91,
      tween: {
        alpha: 0,
        scale: 1.8,
        duration: 250,
        ease: 'Power2'
      },
      scatter: { count: 2, radius: 10 }
    },
    // Bright tip spark
    {
      type: 'circle',
      radius: 2,
      color: 0xFFEECC,
      alpha: 0.9,
      depth: 92,
      tween: {
        alpha: 0,
        scale: 0.5,
        duration: 120,
        ease: 'Power1'
      }
    }
  ]
};

/**
 * Multi arrow burst - fan-shaped burst for multi-shot muzzle flash
 */
const MULTI_ARROW_BURST: VFXEffectConfig = {
  id: 'multi_arrow_burst',
  name: 'Multi Arrow Burst',
  category: 'burst',
  description: 'Fan-shaped burst for multi-shot muzzle flash',
  primitives: [
    // Central flash ring (warm golden burst)
    {
      type: 'circle',
      radius: 18,
      color: 0xDEB887,
      alpha: 0.7,
      depth: 94,
      tween: {
        alpha: 0,
        scale: 2.2,
        duration: 250,
        ease: 'Power2'
      }
    },
    // Arrow-shaped streaks radiating out
    {
      type: 'rect',
      width: 28,
      height: 3,
      color: 0xCD853F,
      alpha: 0.9,
      depth: 95,
      tween: {
        alpha: 0,
        scaleX: 2.5,
        duration: 200,
        ease: 'Power2'
      },
      scatter: { count: 5, radius: 35 }
    },
    // White sparkle tips at endpoints
    {
      type: 'star',
      points: 4,
      innerRadius: 2,
      outerRadius: 5,
      color: 0xFFFFFF,
      alpha: 0.8,
      depth: 96,
      tween: {
        rotation: Math.PI * 0.25,
        alpha: 0,
        duration: 300,
        ease: 'Power2'
      },
      scatter: { count: 5, radius: 30 }
    }
  ]
};

/**
 * Explosion - fiery burst with shockwave and debris
 */
const EXPLOSION: VFXEffectConfig = {
  id: 'explosion',
  name: 'Explosion',
  category: 'burst',
  description: 'Fiery burst with shockwave and debris',
  primitives: [
    // Outer shockwave ring (fast expanding, with stroke)
    {
      type: 'circle',
      radius: 45,
      color: 0xFF4500,
      alpha: 0.6,
      depth: 94,
      stroke: { color: 0xFF6600, width: 3 },
      tween: {
        alpha: 0,
        scale: 3,
        duration: 450,
        ease: 'Power2'
      }
    },
    // Inner fireball (yellow-white hot core)
    {
      type: 'circle',
      radius: 22,
      color: 0xFFFF00,
      alpha: 0.9,
      depth: 96,
      tween: {
        alpha: 0,
        scale: 2.5,
        duration: 300,
        ease: 'Power2'
      }
    },
    // Bright white flash (center)
    {
      type: 'circle',
      radius: 10,
      color: 0xFFFFFF,
      alpha: 1,
      depth: 97,
      tween: {
        alpha: 0,
        scale: 1.5,
        duration: 150,
        ease: 'Power1'
      }
    },
    // Flying debris/ember scatter
    {
      type: 'circle',
      radius: 4,
      color: 0xFF3300,
      alpha: 0.8,
      depth: 95,
      tween: {
        alpha: 0,
        scale: 0.3,
        duration: 600,
        ease: 'Power2'
      },
      scatter: { count: 14, radius: 70 }
    },
    // Star-shaped sparks
    {
      type: 'star',
      points: 4,
      innerRadius: 3,
      outerRadius: 7,
      color: 0xFFAA00,
      alpha: 0.9,
      depth: 96,
      tween: {
        rotation: Math.PI * 0.5,
        alpha: 0,
        duration: 400,
        ease: 'Power2'
      },
      scatter: { count: 6, radius: 50 }
    }
  ],
  screenShake: { intensity: 12, duration: 350 }
};

/**
 * Agility aura - green wind aura with streaking particles
 */
const AGILITY_AURA: VFXEffectConfig = {
  id: 'agility_aura',
  name: 'Agility Aura',
  category: 'aura',
  description: 'Green wind aura with streaking particles',
  followTarget: 'player',
  primitives: [
    // Pulsing ellipse shell
    {
      type: 'ellipse',
      width: 65,
      height: 75,
      color: 0x00FF00,
      alpha: 0.12,
      depth: 50,
      stroke: { color: 0x66FF66, width: 2 },
      tween: {
        rotation: Math.PI * 0.1,
        scale: 1.06,
        duration: 150,
        ease: 'Quad.easeInOut',
        yoyo: true,
        repeat: -1
      }
    },
    // Rising sparkle particles
    {
      type: 'circle',
      radius: 4,
      color: 0x00FF00,
      alpha: 0.6,
      depth: 51,
      tween: {
        alpha: 0,
        y: -20,
        scale: 1.5,
        duration: 500,
        ease: 'Power2'
      },
      scatter: { count: 6, radius: 40 }
    },
    // Wind streak lines (horizontal dashes that sweep past)
    {
      type: 'rect',
      width: 12,
      height: 1,
      color: 0xAAFFAA,
      alpha: 0.6,
      depth: 52,
      tween: {
        x: 40,
        alpha: 0,
        scaleX: 3,
        duration: 400,
        repeat: -1,
        delay: 100
      },
      scatter: { count: 4, radius: 30 }
    }
  ]
};

/**
 * Eagle Eye focus — a readable, persistent precision aura.
 *
 * Eagle Eye previously reused the low-alpha green Agility Aura, so its
 * montage was nearly identical to an idle frame. The warm eye/reticle motif
 * is intentionally distinct from movement buffs and remains bounded by the
 * status-effect duration.
 */
const EAGLE_EYE_FOCUS: VFXEffectConfig = {
  id: 'eagle_eye_focus',
  name: 'Eagle Eye Focus',
  category: 'aura',
  description: 'Gold precision eye and reticle around the focused hunter',
  followTarget: 'player',
  primitives: [
    {
      type: 'ellipse', width: 92, height: 62, color: 0xFFCC44, alpha: 0.16, depth: 49, glow: true,
      stroke: { color: 0xFFD966, width: 3 },
      tween: { alpha: 0.3, scale: 1.08, duration: 650, ease: 'Quad.easeInOut', yoyo: true, repeat: -1 },
    },
    {
      type: 'ellipse', width: 50, height: 22, offsetY: -8, color: 0xFFE8A3, alpha: 0.18, depth: 50,
      stroke: { color: 0xFFF0B8, width: 2 },
      tween: { scaleX: 1.1, duration: 900, ease: 'Quad.easeInOut', yoyo: true, repeat: -1 },
    },
    {
      type: 'circle', radius: 7, offsetY: -8, color: 0xFFF4C2, alpha: 0.92, depth: 51, glow: true,
      tween: { alpha: 0.58, scale: 0.8, duration: 520, ease: 'Power1', yoyo: true, repeat: -1 },
    },
    {
      type: 'rect', width: 34, height: 2, offsetX: -42, offsetY: -8, color: 0xFFD966, alpha: 0.7, depth: 50,
      tween: { alpha: 0.25, duration: 700, yoyo: true, repeat: -1 },
    },
    {
      type: 'rect', width: 34, height: 2, offsetX: 42, offsetY: -8, color: 0xFFD966, alpha: 0.7, depth: 50,
      tween: { alpha: 0.25, duration: 700, yoyo: true, repeat: -1 },
    },
    {
      type: 'star', points: 4, innerRadius: 3, outerRadius: 9, offsetY: -48,
      color: 0xFFFFFF, alpha: 0.9, depth: 52, glow: true,
      tween: { rotation: Math.PI * 0.5, alpha: 0.45, scale: 1.35, duration: 800, yoyo: true, repeat: -1 },
    },
  ],
};

/**
 * Pierce trail - blazing gold power streak for piercing arrow
 */
const PIERCE_TRAIL: VFXEffectConfig = {
  id: 'pierce_trail',
  name: 'Pierce Trail',
  category: 'burst',
  description: 'Blazing gold power streak for piercing arrow',
  primitives: [
    // Wide outer glow (golden, low alpha, expanding)
    {
      type: 'rect',
      width: 120,
      height: 12,
      color: 0xFFD700,
      alpha: 0.25,
      depth: 89,
      tween: {
        alpha: 0,
        scaleX: 1.8,
        scaleY: 0.2,
        duration: 350,
        ease: 'Power2'
      }
    },
    // Core streak (bright white, sharp)
    {
      type: 'rect',
      width: 100,
      height: 4,
      color: 0xFFFFFF,
      alpha: 0.9,
      depth: 90,
      tween: {
        scaleY: 0,
        alpha: 0,
        duration: 300,
        ease: 'Power2'
      }
    },
    // Gold sparks trailing behind
    {
      type: 'star',
      points: 4,
      innerRadius: 2,
      outerRadius: 6,
      color: 0xFFD700,
      alpha: 0.8,
      depth: 91,
      tween: {
        rotation: Math.PI * 0.5,
        alpha: 0,
        scale: 0.5,
        duration: 400,
        ease: 'Power2'
      },
      scatter: { count: 4, radius: 15 }
    },
    // Bright center flash at muzzle
    {
      type: 'circle',
      radius: 8,
      color: 0xFFFFEE,
      alpha: 0.8,
      depth: 92,
      tween: {
        alpha: 0,
        scale: 2.5,
        duration: 200,
        ease: 'Power1'
      }
    }
  ]
};

/**
 * Arrow rain zone - storm of falling arrows with ground impact zone
 */
const ARROW_RAIN_ZONE: VFXEffectConfig = {
  id: 'arrow_rain',
  name: 'Arrow Rain',
  category: 'zone',
  description: 'Storm of falling arrows with ground impact zone',
  // Zone ceiling = the AOE's 6 ticks × 500ms (RainOfArrowsAbility.ts);
  // play-time overrides still win when the AOE path passes one.
  duration: 3000,
  primitives: [
    // Ground shadow & dust bed on the floor plane (offsetY: 20 aligns with feet/ground)
    {
      type: 'ellipse',
      width: 240,
      height: 40,
      color: 0x3d2817,
      alpha: 0.25,
      offsetY: 20,
      depth: 10,
      tween: {
        alpha: 0.4,
        scale: 1.05,
        duration: 600,
        yoyo: true,
        repeat: -1,
        ease: 'Sine.easeInOut',
      },
    },
    // Primary falling arrow shafts — raining straight down from the sky (offsetY: -400)
    {
      type: 'rect',
      width: 3,
      height: 45,
      color: 0x8b4513,
      alpha: 0.95,
      depth: 95,
      offsetY: -400,
      tween: {
        alpha: 0,
        duration: 280,
        repeat: -1,
        delay: 40,
        ease: 'Power2.easeIn',
      },
      scatter: { count: 14, radius: 115 },
    },
    // Secondary faster feathered shafts — staggered timing for rain depth
    {
      type: 'rect',
      width: 2,
      height: 35,
      color: 0xd2b48c,
      alpha: 0.85,
      depth: 95,
      offsetY: -380,
      tween: {
        alpha: 0,
        duration: 250,
        repeat: -1,
        delay: 110,
        ease: 'Power2.easeIn',
      },
      scatter: { count: 10, radius: 95 },
    },
    // Impact sparks on the ground
    {
      type: 'circle',
      radius: 3,
      color: 0xffffff,
      alpha: 0.8,
      offsetY: 20,
      depth: 96,
      tween: {
        y: -18,
        alpha: 0,
        scale: 0.3,
        duration: 250,
        repeat: -1,
        delay: 80,
      },
      scatter: { count: 8, radius: 100 },
    },
    // Ground wood splinters bursting on arrow impact
    {
      type: 'rect',
      width: 2,
      height: 8,
      color: 0x8b4513,
      alpha: 0.8,
      offsetY: 20,
      depth: 96,
      tween: {
        y: -12,
        alpha: 0,
        duration: 300,
        repeat: -1,
        delay: 120,
      },
      scatter: { count: 6, radius: 90 },
    },
    // Dust clouds at impact points along the floor plane
    {
      type: 'circle',
      radius: 10,
      color: 0xbb9966,
      alpha: 0.35,
      offsetY: 20,
      depth: 11,
      tween: {
        alpha: 0,
        scale: 2.2,
        duration: 550,
        repeat: -1,
        delay: 180,
      },
      scatter: { count: 6, radius: 95 },
    },
  ],
};

/**
 * Poison cloud - toxic green cloud with bubbling particles
 */
const POISON_CLOUD_SMALL: VFXEffectConfig = {
  id: 'poison_cloud',
  name: 'Poison Cloud',
  category: 'zone',
  description: 'Toxic green cloud with bubbling particles',
  // World-anchored trigger cloud (snare_trap vfxTrigger + rogue poison_cloud
  // zone, duration_ms 6000). Was the ice_wall leak class: looping, no cap.
  duration: 6000,
  primitives: [
    // Outer haze (large, pulsing, low alpha)
    {
      type: 'circle',
      radius: 65,
      color: 0x00CC00,
      alpha: 0.15,
      depth: 14,
      tween: {
        scale: 1.3,
        alpha: 0.05,
        duration: 2000,
        yoyo: true,
        repeat: -1,
        ease: 'Sine.easeInOut'
      }
    },
    // Inner cloud (denser, slightly rotating)
    {
      type: 'circle',
      radius: 40,
      color: 0x00FF00,
      alpha: 0.25,
      depth: 15,
      tween: {
        scale: 1.15,
        alpha: 0.1,
        rotation: Math.PI * 0.1,
        duration: 1200,
        yoyo: true,
        repeat: -1,
        ease: 'Quad.easeInOut'
      }
    },
    // Toxic bubbles rising (small circles drifting upward)
    {
      type: 'circle',
      radius: 4,
      color: 0x66FF66,
      alpha: 0.7,
      depth: 17,
      tween: {
        y: -25,
        alpha: 0,
        scale: 1.5,
        duration: 800,
        repeat: -1
      },
      scatter: { count: 6, radius: 35 }
    },
    // Dark spore particles drifting sideways
    {
      type: 'circle',
      radius: 6,
      color: 0x006600,
      alpha: 0.5,
      depth: 16,
      tween: {
        x: 30,
        alpha: 0,
        scale: 0.5,
        duration: 1200,
        repeat: -1
      },
      scatter: { count: 4, radius: 45 }
    }
  ]
};

/**
 * Trap placement - ground circle indicator and arming sparks
 */
const TRAP_PLACEMENT: VFXEffectConfig = {
  id: 'trap_placement',
  name: 'Trap Placement',
  category: 'burst',
  description: 'Mechanical click and arm indicator for trap placement',
  primitives: [
    // Ground circle indicator
    {
      type: 'circle',
      radius: 30,
      color: 0xFF6600,
      alpha: 0.3,
      depth: 10,
      stroke: { color: 0xFF4400, width: 2 },
      tween: {
        alpha: 0,
        scale: 1.5,
        duration: 400,
        ease: 'Power2'
      }
    },
    // Arming sparks
    {
      type: 'star',
      points: 4,
      innerRadius: 2,
      outerRadius: 5,
      color: 0xFFAA00,
      alpha: 0.8,
      depth: 95,
      tween: {
        rotation: Math.PI * 0.25,
        alpha: 0,
        scale: 0.5,
        duration: 300,
        ease: 'Power2'
      },
      scatter: { count: 3, radius: 15 }
    }
  ]
};

// ─── Armed-trap zone family (zone-def re-categorization batch) ───────────────
// Persistent armed-trap telegraphs — every id below was unregistered (legacy
// particle fallback) before this batch. TRAP_PLACEMENT stays the one-shot
// arming click on cast (the split: burst = placement beat, zone = armed
// lifetime). All are category:'zone' under the wave-4 lifetime contract:
// world-anchored, looping legal only under the mandatory duration ceiling
// (PF-ZONE-NO-DURATION) + the tracked-persistent stop path.

/**
 * Freezing Trap — armed frost snare. Icy rim shards + crystal glint.
 * Duration = the SUMMON's 30s (FreezingTrapAbility.ts); the 8s TOML
 * duration_ms mismatch is a content-lane fix.
 */
const FREEZING_TRAP_ZONE: VFXEffectConfig = {
  id: 'freezing_trap',
  name: 'Freezing Trap',
  category: 'zone',
  description: 'Armed frost trap waiting to snap shut',
  duration: 30000,
  primitives: [
    ...groundZone({
      ring: { width: 110, fill: 0x66BBEE, rim: 0xCCEEFF, alpha: 0.14, pulse: { alpha: 0.24, duration: 1600 } },
      edge: { count: 5, rimRadius: 50, shape: 'star', size: 5, color: 0xAADDFF, alpha: 0.75, speed: 0.12, glow: true },
    }),
    // Crystal glint at the trigger plate — slow cold flicker
    centerpiece({
      shape: 'star', width: 8,
      color: 0xFFFFFF, accent: 0x88CCFF, glow: true, depth: 14, offsetY: 14,
      flicker: { alpha: 0.4, scale: 1.3, duration: 1200 },
    }),
  ],
};

/**
 * Snare Trap — armed rope snare, taut and toothy. 4s gameplay duration.
 */
const SNARE_TRAP_ZONE: VFXEffectConfig = {
  id: 'snare_trap',
  name: 'Snare Trap',
  category: 'zone',
  description: 'Armed snare trap with taut rope perimeter',
  duration: 4000,
  primitives: [
    ...groundZone({
      ring: { width: 100, fill: 0x8B6F47, rim: 0xC9A66B, alpha: 0.18, pulse: { alpha: 0.28, duration: 900 } },
      edge: { count: 6, rimRadius: 45, shape: 'rect', size: 4, color: 0x999999, alpha: 0.8, speed: 0.2 },
    }),
    centerpiece({
      shape: 'star', width: 7,
      color: 0xC9A66B, accent: 0x6B4F2A, depth: 14, offsetY: 14,
      flicker: { alpha: 0.5, duration: 700 },
    }),
  ],
};

/**
 * Tar Trap — viscous black pool, slow heavy bubbles. 30s TOML duration.
 */
const TAR_TRAP_ZONE: VFXEffectConfig = {
  id: 'tar_trap',
  name: 'Tar Trap',
  category: 'zone',
  description: 'Viscous tar pool that slows anything crossing it',
  duration: 30000,
  primitives: [
    ...groundZone({
      ring: { width: 130, fill: 0x1A1208, rim: 0x4A3319, alpha: 0.5, rimWidth: 3, pulse: { alpha: 0.65, scale: 1.03, duration: 2200 } },
      fall: { direction: 'up', count: 4, spread: 55, radius: 4, color: 0x332211, alpha: 0.8, travel: 10, periodMs: 1800 },
    }),
    // Oily sheen highlight — the second tone that keeps the pool readable
    zoneRing({ width: 70, fill: 0x3A2A14, rim: 0x6B5230, alpha: 0.4, rimWidth: 1, depth: 11, pulse: { alpha: 0.55, duration: 1600 } }),
  ],
};

/**
 * Steel Trap — sprung steel jaws glinting on the plate. 20s TOML duration.
 */
const STEEL_TRAP_ZONE: VFXEffectConfig = {
  id: 'steel_trap',
  name: 'Steel Trap',
  category: 'zone',
  description: 'Armed steel jaws waiting on the trigger plate',
  duration: 20000,
  primitives: [
    ...groundZone({
      ring: { width: 90, fill: 0x777777, rim: 0xBBBBBB, alpha: 0.16, pulse: { alpha: 0.26, duration: 1300 } },
    }),
    // The jaws — twin steel teeth arcs as a star glint pair
    centerpiece({
      shape: 'star', width: 10,
      color: 0xCCCCCC, accent: 0x888888, depth: 14, offsetY: 14,
      flicker: { alpha: 0.45, scale: 1.2, duration: 1000 },
    }),
    centerpiece({
      shape: 'rect', width: 26, height: 4,
      color: 0x999999, accent: 0xDDDDDD, alpha: 0.7, depth: 13, offsetY: 16,
      flicker: { alpha: 0.5, duration: 1300 },
    }),
  ],
};

/**
 * Net Trap — coiled rope net, woven tan ring. 6s TOML duration.
 */
const NET_TRAP_ZONE: VFXEffectConfig = {
  id: 'net_trap',
  name: 'Net Trap',
  category: 'zone',
  description: 'Coiled net trap ready to entangle',
  duration: 6000,
  primitives: [
    ...groundZone({
      ring: { width: 105, fill: 0xB89B5E, rim: 0xE0C88A, alpha: 0.16, pulse: { alpha: 0.26, duration: 1100 } },
      edge: { count: 8, rimRadius: 48, shape: 'circle', size: 3, color: 0x8A7442, alpha: 0.8, speed: 0.15 },
    }),
    // Woven inner coil — second tone
    zoneRing({ width: 55, fill: 0x9A7F4A, rim: 0xD8BC7E, alpha: 0.2, rimWidth: 1, depth: 11 }),
  ],
};

/**
 * Arrow-rain placement id — AOEBehavior places the storm with
 * vfxId 'arrow_rain_zone' (RainOfArrowsAbility.ts); previously unregistered
 * → legacy particle fallback. Same def, placement id, so the actual AOE
 * placement renders the zone (its play-time duration override wins).
 */
const ARROW_RAIN_ZONE_PLACEMENT: VFXEffectConfig = {
  ...ARROW_RAIN_ZONE,
  id: 'arrow_rain_zone',
  description: 'Arrow rain storm as placed by the AOE path',
};

/**
 * Aimed shot glow - golden charge glow for aimed shot wind-up
 */
const AIMED_SHOT_GLOW: VFXEffectConfig = {
  id: 'aimed_shot_glow',
  name: 'Aimed Shot Glow',
  category: 'burst',
  description: 'Golden charge glow for aimed shot wind-up',
  primitives: [
    // Outer golden charge ring
    { type: 'circle', radius: 22, color: 0xFFD700, alpha: 0.5, depth: 94,
      tween: { alpha: 0, scale: 1.8, duration: 400, ease: 'Power2' } },
    // Inner bright core (narrowing focus)
    { type: 'circle', radius: 8, color: 0xFFFFCC, alpha: 0.9, depth: 96,
      tween: { alpha: 0, scale: 0.5, duration: 300, ease: 'Power2' } },
    // Crosshair lines converging
    { type: 'rect', width: 30, height: 2, color: 0xFFD700, alpha: 0.7, depth: 95,
      tween: { alpha: 0, scaleX: 0.3, duration: 350, ease: 'Power2' } },
    { type: 'rect', width: 2, height: 30, color: 0xFFD700, alpha: 0.7, depth: 95,
      tween: { alpha: 0, scaleY: 0.3, duration: 350, ease: 'Power2' } }
  ]
};

/**
 * Ranged projectile - basic ranged attack muzzle flash
 */
const RANGED_PROJECTILE: VFXEffectConfig = {
  id: 'ranged_projectile',
  name: 'Ranged Projectile',
  category: 'burst',
  description: 'Basic muzzle flash for ranged attacks',
  primitives: [
    // Muzzle flash ring
    { type: 'circle', radius: 12, color: 0xDEB887, alpha: 0.7, depth: 95,
      tween: { alpha: 0, scale: 2, duration: 200, ease: 'Power2' } },
    // Wind puff
    { type: 'circle', radius: 5, color: 0xFFFFFF, alpha: 0.5, depth: 94,
      tween: { alpha: 0, scale: 1.8, duration: 250, ease: 'Power2' },
      scatter: { count: 2, radius: 8 } },
    // Bright tip
    { type: 'circle', radius: 3, color: 0xFFEECC, alpha: 0.9, depth: 96,
      tween: { alpha: 0, scale: 0.5, duration: 120, ease: 'Power1' } }
  ]
};

// =============================================================================
// EXPORTS
// =============================================================================


/**
 * Jump Back (disengage) — hunter escape kick. SPEC: dust kicks at the feet
 * and thin horizontal motion streaks trail the departure direction; earthy
 * whites/tans, fast (≤300ms). Reads as movement, not damage.
 */
const JUMP_BACK: VFXEffectConfig = {
  id: 'jump_back',
  name: 'Jump Back',
  category: 'burst',
  description: 'Disengage escape — dust kick + motion streaks',
  primitives: [
    { type: 'particles', particleEffectId: 'landing_dust', particleCount: 6 },
    // Ground dust puff.
    { type: 'circle', radius: 16, color: 0xC9B896, alpha: 0.5, depth: 93,
      tween: { alpha: 0, scale: 2.0, duration: 300, ease: 'Power2' } },
    // Motion streaks — thin horizontal lines stretching away.
    { type: 'rect', width: 26, height: 3, color: 0xFFFFFF, alpha: 0.7, depth: 95,
      tween: { alpha: 0, scaleX: 2.2, duration: 240, ease: 'Power1' },
      scatter: { count: 3, radius: 14 } },
  ],
};

/**
 * Rapid Fire Glow (channeled ultimate) — red-orange charge aura. SPEC: a
 * pulsing hot ring holds on the caster (yoyo) while speed-line streaks flick
 * outward; authored #ff4400 identity, reads as "arrows about to fly".
 */
const RAPID_FIRE_GLOW: VFXEffectConfig = {
  id: 'rapid_fire_glow',
  name: 'Rapid Fire Glow',
  category: 'burst',
  description: 'Rapid Fire channel charge — pulsing hot ring + speed lines',
  primitives: [
    { type: 'particles', particleEffectId: 'hit_spark', particleCount: 8 },
    // Pulsing charge ring (yoyo breathe while the channel winds up).
    { type: 'circle', radius: 24, color: 0xFF4400, alpha: 0.55, depth: 94,
      tween: { alpha: 0.85, scale: 1.25, duration: 220, ease: 'Power1', yoyo: true, repeat: 2 } },
    // Speed lines — thin streaks flicking outward.
    { type: 'rect', width: 20, height: 2, color: 0xFFAA66, alpha: 0.85, depth: 96,
      tween: { alpha: 0, scaleX: 2.6, duration: 320, ease: 'Power2' },
      scatter: { count: 5, radius: 20 } },
    // Hot core.
    { type: 'circle', radius: 8, color: 0xFFDD99, alpha: 0.9, depth: 95,
      tween: { alpha: 0, scale: 1.6, duration: 420, ease: 'Power2' } },
  ],
};

/**
 * Death Chakram launch — F6 hunter-lane review found this the ONLY hunter
 * projectile with no cast-side visual. A spinning nature disc winds up at
 * the caster's hand for the launch beat; the projectile itself (green disc,
 * trail) is TOML-styled and takes over from here.
 */
const DEATH_CHAKRAM_LAUNCH: VFXEffectConfig = {
  id: 'death_chakram_launch',
  name: 'Death Chakram Launch',
  category: 'burst',
  description: 'Spinning nature disc wind-up at the caster hand',
  primitives: [
    // The disc — fast spin-up, slight grow, quick release fade
    {
      type: 'ellipse', width: 22, height: 22, offsetX: 14, offsetY: -6,
      color: 0x44FF44, alpha: 0.85, depth: 60, glow: true,
      stroke: { color: 0x99FF66, width: 2 },
      tween: { rotation: Math.PI * 4, scale: 1.25, alpha: 0, duration: 380, ease: 'Power2' },
    },
    // Serrated edge read — thin rects riding the disc rim
    {
      type: 'rect', width: 8, height: 2, color: 0xBBFF88, alpha: 0.9, depth: 61,
      orbit: { radius: 12, speed: 2.4, orientToPath: true },
      tween: { alpha: 0, duration: 380 },
    },
    // Release glint
    {
      type: 'star', points: 4, innerRadius: 3, outerRadius: 8, color: 0xDDFFCC, alpha: 0.95, depth: 62, glow: true,
      offsetX: 14, offsetY: -6,
      tween: { alpha: 0, scale: 1.8, duration: 240, ease: 'Power1', delay: 180 },
    },
  ],
};

export const RANGER_VFX: VFXEffectConfig[] = [
  JUMP_BACK,
  DEATH_CHAKRAM_LAUNCH,
  RAPID_FIRE_GLOW,
  ARROW_WHIZ,
  MULTI_ARROW_BURST,
  EXPLOSION,
  AGILITY_AURA,
  EAGLE_EYE_FOCUS,
  PIERCE_TRAIL,
  ARROW_RAIN_ZONE,
  ARROW_RAIN_ZONE_PLACEMENT,
  POISON_CLOUD_SMALL,
  TRAP_PLACEMENT,
  FREEZING_TRAP_ZONE,
  SNARE_TRAP_ZONE,
  TAR_TRAP_ZONE,
  STEEL_TRAP_ZONE,
  NET_TRAP_ZONE,
  AIMED_SHOT_GLOW,
  RANGED_PROJECTILE,
];
